import React from "react";
import {BrowserRouter, Navigate, Route, Routes, useLocation} from "react-router-dom";

/* Global styles loaded via src/styles/main.css in index.js */
import Home from "./component/Home";
import About from "./component/About";
import Footer from "./Footer";
import Assets from "./component/AssetManagement/Assets";
import Reports from "./component/Reports";
import Users from "./component/UserManagement/Users";
import Tasks from "./component/TaskManagement/Tasks";
import Profile from "./component/Profile";
import Settings from "./component/Settings";
import Tracking from "./component/Tracking/Tracking";
import PrivacyPolicy from "./component/PrivacyPolicy";
import TermsConditions from "./component/TermsConditions";
import ForgotPassword from "./component/ForgotPassword";
import SetInitialPassword from "./component/SetInitialPassword";
import * as Constants from "./utils/Constants";
import LandingPage from "./component/LandingPage";
import AuditLog from "./component/AuditLog/AuditLog";
import DeviceCommandCenter from "./component/CommandCenter/DeviceCommandCenter";
import ScheduledUnlocks from "./component/ScheduledUnlocks/ScheduledUnlocks";
import Trips from "./component/TripManagement/Trips";
import OpenInApp from "./component/OpenInApp";
import {PERMISSIONS, getRole, landingPathForRole, needsInitialPassword} from "./utils/rolePermissions";
import CommandPalette from "./components/overlays/CommandPalette";
import {useSessionExpiryGuard} from "./hooks/useSessionExpiryGuard";
import {refreshWebPush} from "./utils/webPush";

/* The landing page renders its own full footer (lp-footer) with copyright
   and legal links, so the global fixed Footer would double up and — since
   the landing page is a plain scrolling page with no bottom padding
   reserved for a fixed bar — overlap its content while scrolling. */
const AppFooter = () => {
  const { pathname } = useLocation();
  return pathname === "/" ? null : <Footer />;
};

function App() {
  // Browser vehicle alerts: re-bind this browser's push token to whoever is signed in (no-op
  // unless web push is configured and the person allowed notifications here).
  React.useEffect(() => {
    if (localStorage.getItem(Constants.IS_AUTHENTICATED)) refreshWebPush();
  }, []);

  const PrivateRoute = ({ element }) => {
    const isAuthenticated = localStorage.getItem(Constants.IS_AUTHENTICATED);
    // Proactive, no-network expiry check — see useSessionExpiryGuard for why this exists
    // (a stale dashboard that only logs out once *something* happens to 401, or on a manual
    // refresh). Runs on every protected route, so every page — not just /home — is covered.
    useSessionExpiryGuard(!!isAuthenticated);
    if (!isAuthenticated) return <Navigate to="/" replace />;
    // A Track Point driver whose account has no real password yet (see
    // rolePermissions.needsInitialPassword) must set one before reaching anywhere else —
    // otherwise they're stuck: Change Password needs an old password they can't know, and
    // there was previously no other way to ever get a working password on the web client.
    // Checked on every protected route (not just the post-login redirect), so a driver who
    // reloads or navigates away mid-setup is routed straight back here — mirrors the Android
    // app's PostLoginSetup.navigateByRole doing the same check on its auto-login path.
    if (needsInitialPassword() && window.location.pathname !== "/set-initial-password") {
      return <Navigate to="/set-initial-password" replace />;
    }
    return element;
  };

  /* Checks authentication AND that the user's role has the given permission key. Falls back to
     the role's own landing page rather than a hardcoded "/home" — now that /home itself is
     permission-guarded (see below), a denied role landing back on /home would just get denied
     again, looping forever. landingPathForRole is the same mapping login uses, so a role always
     bounces to somewhere it's actually allowed to be. */
  const RoleRoute = ({ element, permission }) => {
    const role = getRole();
    const allowed = Array.isArray(PERMISSIONS[permission]) && PERMISSIONS[permission].includes(role);
    return allowed ? element : <Navigate to={landingPathForRole(role)} replace />;
  };

  const guarded = (element, permission) => (
    <PrivateRoute element={<RoleRoute element={element} permission={permission} />} />
  );

  return (
    <div className="App">
      <BrowserRouter>
        {/* Global Cmd/Ctrl+K command palette — opens on the keybind on every
            authenticated page; renders null on / and /forgot-password. */}
        <CommandPalette />
        <Routes>
          {/* The standalone login page is retired — sign-in lives on the
              landing page. Legacy /login bookmarks land there too. */}
          <Route path="/login"              element={<Navigate to="/" replace />} />
          <Route path="/forgot-password"    element={<ForgotPassword />} />
          <Route path="/"                   element={<LandingPage />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
          <Route path="/privacy-policy"     element={<PrivacyPolicy />} />
          {/* Daily fleet summary WhatsApp/email button → opens the Android app (see OpenInApp). */}
          <Route path="/app/:target"        element={<OpenInApp />} />

          {/* /home (the fleet Dashboard) is role-guarded — Track Point (MOBI_TRACK) must never
              reach it (every device across the client, not just its own), only Live Tracking.
              Previously open to any authenticated user with "role-specific content rendered
              inside Home.js" (a comment that was never actually true — Home.js has no MOBI_TRACK
              handling at all), so a Track Point user hitting /home directly saw the full
              unscoped fleet dashboard. */}
          <Route path="/home"           element={guarded(<Home />,                "sidebar_dashboard")} />
          <Route path="/about"          element={<PrivateRoute element={<About />} />} />

          {/* Mandatory one-time step for a Track Point driver with no real password yet — see
              PrivateRoute's needsInitialPassword redirect above. Deliberately not role-gated via
              guarded()/RoleRoute: whether this route is *reachable* is entirely about
              needsInitialPassword(), not sidebar permissions. */}
          <Route path="/set-initial-password" element={<PrivateRoute element={<SetInitialPassword />} />} />

          {/* Role-guarded routes */}
          <Route path="/profile"        element={guarded(<Profile />,             "sidebar_profile")} />
          <Route path="/assets"         element={guarded(<Assets />,              "sidebar_assets")} />
          <Route path="/tracking"       element={guarded(<Tracking />,            "sidebar_tracking")} />
          <Route path="/users"          element={guarded(<Users />,               "sidebar_users")} />
          <Route path="/tasks"          element={guarded(<Tasks />,               "sidebar_tasks")} />
          <Route path="/trips"          element={guarded(<Trips />,               "sidebar_trips")} />
          <Route path="/reports"        element={guarded(<Reports />,             "sidebar_reports")} />
          <Route path="/command-center" element={guarded(<DeviceCommandCenter />, "sidebar_commandCenter")} />
          <Route path="/scheduled-unlocks" element={guarded(<ScheduledUnlocks />, "sidebar_scheduledUnlocks")} />
          <Route path="/settings"       element={guarded(<Settings />,            "sidebar_settings")} />
          <Route path="/audit-log"      element={guarded(<AuditLog />,            "sidebar_auditLog")} />
        </Routes>
        <AppFooter />
      </BrowserRouter>
    </div>
  );
}

export default App;
